import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';
import {
  ReloadOutlined,
  EditOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  SearchOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useAppSelector } from '../../hooks/redux';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { toIsoDate } from '../../utils/dateHelpers';
import { formatCurrencyLocalized } from '../../utils/currencyHelpers';
import { exportCSV, exportExcel, exportPDF, ExportConfig } from '../../utils/reportExport';
import {
  useGetExpensesQuery,
  useLazyGetExpensesQuery,
  useGetExpenseCategoriesQuery,
  useGetExpensePaymentMethodsQuery,
} from '../../services/expensesApi';
import { canManageFinanzas, canSeeAllExpenses, isSupportUser } from '../../utils/roleHelpers';
import { ExpenseCaptureResultDto, ExpenseDto, ExpenseFilter } from '../../types/expense';
import ExpenseCaptureFlow from './ExpenseCaptureFlow';
import ExpenseFormDialog from './ExpenseFormDialog';
import ExpenseEditDialog from './ExpenseEditDialog';
import ExpenseCancelDialog from './ExpenseCancelDialog';
import ExpenseAttachmentsViewer from './ExpenseAttachmentsViewer';

const { Text } = Typography;

type StatusFilterValue = 'active' | 'cancelled' | 'all';

const DEFAULT_PAGE_SIZE = 50;

const STATUS_PILLS: { value: StatusFilterValue; label: string }[] = [
  { value: 'active', label: 'Activos' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'all', label: 'Todos' },
];

interface ExpensesTabProps {
  /** Contador que la barra de la página incrementa para abrir el modal de alta */
  addSignal?: number;
  /** Ídem, para el flujo de captura desde el comprobante */
  captureSignal?: number;
}

const ExpensesTab: React.FC<ExpensesTabProps> = ({ addSignal, captureSignal }) => {
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { user } = useAppSelector((state) => state.auth);
  const restaurantId = currentRestaurant?.id;
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { isMobile } = useResponsive();
  const { message } = App.useApp();

  // ----- Filters state -----
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(() => {
    // Default: month-to-date.
    return [dayjs().startOf('month'), dayjs()];
  });
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('active');
  const [paidFromCash, setPaidFromCash] = useState<boolean | undefined>(undefined);
  // GUID of a specific method, or the literal string "none" to filter expenses without
  // a payment method assigned. Backend translates "none" to PaymentMethodId IS NULL.
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string | undefined>(undefined);
  // undefined = todos, 'no' = sin comprobante, 'yes' = con comprobante.
  const [attachmentFilter, setAttachmentFilter] = useState<'yes' | 'no' | undefined>(undefined);
  // Gasto cuyo visor de comprobantes está abierto (clic en el clip de la fila).
  const [viewingAttachmentsOf, setViewingAttachmentsOf] = useState<ExpenseDto | null>(null);
  const [search, setSearch] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  // Caja y método de pago se pliegan tras "+ Filtros"; abre solo si ya hay uno activo
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  // ----- Dialog state -----
  const [createOpen, setCreateOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  // Lo que el sistema leyó de la foto. Viaja al form para precargarlo y, sobre todo, lleva
  // el captureId: sin él el gasto se guardaría sin su comprobante adjunto.
  const [capture, setCapture] = useState<ExpenseCaptureResultDto | null>(null);
  const [editingExpense, setEditingExpense] = useState<ExpenseDto | null>(null);
  const [cancellingExpense, setCancellingExpense] = useState<ExpenseDto | null>(null);

  // La barra de la página incrementa addSignal con "Nuevo gasto"
  useEffect(() => {
    if (addSignal && addSignal > 0) {
      setCreateOpen(true);
    }
  }, [addSignal]);

  // Y captureSignal con "Capturar ticket". El botón vive arriba, junto al de alta
  // manual — ver el comentario de la barra en pages/Finanzas.tsx.
  useEffect(() => {
    if (captureSignal && captureSignal > 0) {
      setCaptureOpen(true);
    }
  }, [captureSignal]);

  const filter: ExpenseFilter = useMemo(
    () => ({
      // Send date-only (yyyy-MM-dd) so the backend interprets in the restaurant's tz,
      // not the browser's. Backend converts `from` to local-midnight UTC and `to` to
      // next-day local-midnight UTC (exclusive) — same convention as CreateAsync.
      from: dateRange?.[0] ? toIsoDate(dateRange[0]) : undefined,
      to: dateRange?.[1] ? toIsoDate(dateRange[1]) : undefined,
      categoryId,
      status: statusFilter,
      paidFromCash,
      paymentMethodId: paymentMethodFilter,
      hasAttachment: attachmentFilter === undefined ? undefined : attachmentFilter === 'yes',
      search: search.trim() || undefined,
      pageNumber,
      pageSize,
    }),
    [
      dateRange,
      categoryId,
      statusFilter,
      paidFromCash,
      paymentMethodFilter,
      attachmentFilter,
      search,
      pageNumber,
      pageSize,
    ]
  );

  const {
    data: expensesPage,
    isFetching,
    isError,
    refetch,
  } = useGetExpensesQuery(
    { restaurantId: restaurantId!, filter },
    { skip: !restaurantId }
  );

  const { data: categories } = useGetExpenseCategoriesQuery(
    { restaurantId: restaurantId! },
    { skip: !restaurantId }
  );

  const { data: paymentMethods } = useGetExpensePaymentMethodsQuery(
    { restaurantId: restaurantId! },
    { skip: !restaurantId }
  );

  const hasNoCategories = categories !== undefined && categories.length === 0;
  const summary = expensesPage?.summary;

  // ----- Export -----
  // The table is paginated, but the user asked to export everything matching the
  // current filters — so we fire a one-off fetch with a large pageSize rather than
  // exporting only the visible page. RTK Query caches it under the same tag, so a
  // re-export with unchanged filters is instant.
  const [triggerExportFetch] = useLazyGetExpensesQuery();
  const [isExporting, setIsExporting] = useState(false);

  const EXPORT_PAGE_SIZE = 5000;

  const handleExport = async (fmt: 'csv' | 'excel' | 'pdf') => {
    if (!restaurantId) return;
    try {
      setIsExporting(true);
      const page = await triggerExportFetch({
        restaurantId,
        filter: { ...filter, pageNumber: 1, pageSize: EXPORT_PAGE_SIZE },
      }).unwrap();

      const rows = page.items;
      if (rows.length === 0) {
        message.info('No hay gastos para exportar con los filtros actuales.');
        return;
      }
      if (page.totalCount > rows.length) {
        // Defensive: extremely unlikely with a 5k page, but never silently truncate.
        message.warning(
          `Se exportaron los primeros ${rows.length} de ${page.totalCount} gastos. Acota el rango de fechas para incluir todos.`
        );
      }

      const activeTotal = rows
        .filter((r) => r.status === 'Active')
        .reduce((sum, r) => sum + r.amount, 0);

      const periodLabel =
        dateRange?.[0] && dateRange?.[1]
          ? `${formatInTimeZone(dateRange[0].toISOString(), 'DD MMM YYYY')} – ${formatInTimeZone(dateRange[1].toISOString(), 'DD MMM YYYY')}`
          : 'Todos los gastos';

      const config: ExportConfig = {
        reportTitle: 'Gastos',
        periodLabel,
        restaurantName: currentRestaurant?.name || 'N/A',
        timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
        summaryCards: [
          { label: 'Gastos', value: String(rows.length) },
          { label: 'Monto total (activos)', value: formatCurrencyLocalized(activeTotal, 'MXN') },
        ],
        sections: [
          {
            title: 'Detalle de gastos',
            titleStyle: 'section',
            headers: [
              'Fecha',
              'Categoría',
              'Proveedor',
              'Método de pago',
              'Desde caja',
              'Caja',
              'Monto',
              'Estado',
              'Notas',
              'Creado por',
            ],
            rows: rows.map((r) => [
              formatInTimeZone(r.expenseDate, 'DD/MM/YYYY'),
              r.categoryName,
              r.vendor || '—',
              r.paymentMethodName || '—',
              r.paidFromCash ? 'Sí' : 'No',
              r.cashRegisterUserName || '—',
              formatCurrencyLocalized(r.amount, 'MXN'),
              r.status === 'Cancelled' ? 'Cancelado' : 'Activo',
              r.notes || '—',
              r.createdByUserName || '—',
            ]),
          },
        ],
      };

      if (fmt === 'csv') exportCSV(config, 'gastos');
      else if (fmt === 'excel') exportExcel(config, 'gastos');
      else exportPDF(config);
    } catch {
      message.error('No se pudieron exportar los gastos. Intenta de nuevo.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportMenuItems = [
    { key: 'csv', label: 'CSV', onClick: () => handleExport('csv') },
    { key: 'excel', label: 'Excel', onClick: () => handleExport('excel') },
    { key: 'pdf', label: 'PDF', onClick: () => handleExport('pdf') },
  ];

  const clearFilters = () => {
    setDateRange([dayjs().startOf('month'), dayjs()]);
    setCategoryId(undefined);
    setStatusFilter('active');
    setPaidFromCash(undefined);
    setPaymentMethodFilter(undefined);
    setAttachmentFilter(undefined);
    setSearch('');
    setPageNumber(1);
  };

  const money = (n: number) => formatCurrencyLocalized(n, 'MXN');

  /**
   * ¿Puede gestionar, o sólo capturar? Ver utils/roleHelpers.canManageFinanzas.
   * Esconder el botón no es la barrera de seguridad — el gate real es el [Authorize]
   * del endpoint. Esto evita que el Gerente se tope con un 403.
   */
  const canManage = canManageFinanzas(user?.role, isSupportUser(user?.email));

  /**
   * ¿Ve todos los gastos del restaurante, o sólo los suyos?
   *
   * El backend YA recorta las filas y omite los totales (ExpensesController.IsOwnScopeOnly),
   * así que esto no protege nada — evita pintar una pantalla que miente. Con alcance
   * propio, los KPIs saldrían vacíos, "Exportar" bajaría cuatro renglones, "pagado desde
   * caja" filtraría sobre una columna que siempre dice lo mismo y "Creado por" repetiría
   * el nombre del usuario en cada fila.
   */
  const seesEverything = canSeeAllExpenses(user?.role, isSupportUser(user?.email));


  // Acceso directo del KPI "Sin comprobante": aplica el filtro de no-comprobados.
  // Con adjuntos opcionales, esta es la única palanca del dueño para perseguirlos.
  const focusNoReceipt = () => {
    setAttachmentFilter('no');
    setMoreFiltersOpen(true);
    setPageNumber(1);
  };

  // Acceso directo del KPI "Sin método": aplica el filtro de no-clasificados
  const focusNoMethod = () => {
    setPaymentMethodFilter('none');
    setMoreFiltersOpen(true);
    setPageNumber(1);
  };

  const cancelPill = <span className="fin-cancel-pill">cancelado</span>;

  // Column strategy: on mobile the single "Fecha" column stacks the essentials
  // (vendor, date·category·method, amount) so rows read as cards without
  // horizontal scroll; desktop gets the full grid.
  const columns: ColumnsType<ExpenseDto> = isMobile
    ? [
        {
          title: 'Gasto',
          key: 'gasto',
          render: (_v, row) => (
            <div style={{ padding: '2px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Text strong style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.vendor || row.categoryName}
                </Text>
                <span
                  className="fin-money"
                  style={row.status === 'Cancelled' ? { textDecoration: 'line-through' } : undefined}
                >
                  {money(row.amount)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {formatInTimeZone(row.expenseDate, 'DD MMM')} · {row.categoryName}
                {row.paymentMethodName ? ` · ${row.paymentMethodName}` : ''}
                {row.paidFromCash ? ` (caja ${row.cashRegisterUserName || '—'})` : ''}
                {/* La columna "Comp." es responsive: ['md'], así que en mobile el
                    contador tiene que vivir aquí. Y mobile es precisamente donde el
                    empleado captura, así que sin esto la feature queda invisible en
                    el dispositivo donde más se usa. */}
                {row.attachmentCount > 0 && (
                  <>
                    {' · '}
                    {/* Button y no un ancla: esto no navega a ningún lado, y un ancla
                        sin href no es accesible por teclado. (Sin markup literal en el
                        comentario: confunde a las herramientas que leen el fuente.) */}
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: 12 }}
                      icon={<PaperClipOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewingAttachmentsOf(row);
                      }}
                    >
                      {row.attachmentCount}
                    </Button>
                  </>
                )}
              </div>
              {row.status === 'Cancelled' && <div style={{ marginTop: 2 }}>{cancelPill}</div>}
            </div>
          ),
        },
        {
          title: '',
          key: 'actions',
          width: 72,
          align: 'right',
          render: (_v, row) =>
            row.status === 'Active' ? (
              <Space size={0}>
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditingExpense(row)} />
                {/* Gemelo del botón de la rama desktop. Las definiciones de columnas
                    están duplicadas (mobile apila todo en una celda), así que la regla
                    de permisos hay que aplicarla en LOS DOS lugares — es la misma clase
                    de omisión que el bloque duplicado de "Agregar nuevo método".
                    Pinneado por FinanzasUiPermissionSweepTests. */}
                {canManage && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => setCancellingExpense(row)}
                  />
                )}
              </Space>
            ) : null,
        },
      ]
    : [
        {
          title: 'Fecha',
          dataIndex: 'expenseDate',
          key: 'expenseDate',
          width: 110,
          render: (v: string) => formatInTimeZone(v, 'DD MMM YYYY'),
        },
        {
          title: 'Categoría',
          dataIndex: 'categoryName',
          key: 'categoryName',
          width: 140,
        },
        {
          title: 'Proveedor / notas',
          key: 'vendorNotes',
          ellipsis: true,
          render: (_v, row) => (
            <div style={{ minWidth: 0 }}>
              <Text strong>{row.vendor || <Text type="secondary">—</Text>}</Text>
              {row.notes && (
                <Tooltip title={row.notes} placement="topLeft">
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.notes}
                  </div>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          title: 'Método',
          key: 'method',
          width: 200,
          render: (_v, row) => (
            <span>
              {row.paymentMethodName || <Text type="secondary">—</Text>}
              {row.paidFromCash && (
                <Text type="secondary"> · caja {row.cashRegisterUserName || '—'}</Text>
              )}
            </span>
          ),
        },
        // Con alcance propio esta columna repite el nombre del usuario en cada fila.
        // Se omite del array en vez de renderizar vacío: una columna con el mismo valor
        // en todos los renglones ocupa ancho y no informa nada.
        ...(seesEverything
          ? [
              {
                title: 'Creado por',
                dataIndex: 'createdByUserName',
                key: 'createdByUserName',
                width: 130,
                responsive: ['xl'] as any,
                render: (v?: string | null) => v || <Text type="secondary">—</Text>,
              },
            ]
          : []),
        {
          title: 'Monto',
          dataIndex: 'amount',
          key: 'amount',
          width: 150,
          align: 'right',
          render: (v: number, row) =>
            row.status === 'Cancelled' ? (
              <Space size={6}>
                <span className="fin-money" style={{ textDecoration: 'line-through' }}>
                  {money(v)}
                </span>
                <Tooltip
                  title={
                    <>
                      {row.cancellationReason && <div>Motivo: {row.cancellationReason}</div>}
                      {row.cancelledByUserName && <div>Por: {row.cancelledByUserName}</div>}
                    </>
                  }
                >
                  {cancelPill}
                </Tooltip>
              </Space>
            ) : (
              <span className="fin-money">{money(v)}</span>
            ),
        },
        {
          title: 'Comp.',
          dataIndex: 'attachmentCount',
          key: 'attachments',
          width: 80,
          align: 'center',
          responsive: ['md'] as any,
          render: (count: number, row) =>
            count > 0 ? (
              <Tooltip title={`${count} comprobante${count !== 1 ? 's' : ''} — clic para ver`}>
                <Button
                  type="text"
                  size="small"
                  onClick={() => setViewingAttachmentsOf(row)}
                  icon={<PaperClipOutlined />}
                >
                  {count}
                </Button>
              </Tooltip>
            ) : (
              <Text type="secondary">—</Text>
            ),
        },
        {
          title: '',
          key: 'actions',
          width: 80,
          align: 'right',
          render: (_v, row) =>
            row.status === 'Active' ? (
              <Space size={0}>
                <Tooltip title="Editar">
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditingExpense(row)} />
                </Tooltip>
                {/* Punto 4 de 7 del barrido: cancelar revierte caja. Su gemelo vive
                    dentro de ExpenseEditDialog — los dos hay que cerrarlos. */}
                {canManage && (
                  <Tooltip title="Cancelar gasto">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => setCancellingExpense(row)}
                    />
                  </Tooltip>
                )}
              </Space>
            ) : null,
        },
      ];

  // ----- Render branches -----
  if (!restaurantId) {
    return <Alert type="warning" message="No hay restaurante seleccionado." />;
  }

  return (
    <div>
      {/* Fila de filtros: estado como pills + esenciales; caja/método tras "+ Filtros" */}
      <div className="fin-filters">
        <div className="fin-pills">
          {STATUS_PILLS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`fin-pill${statusFilter === p.value ? ' fin-pill--selected' : ''}`}
              onClick={() => {
                setStatusFilter(p.value);
                setPageNumber(1);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <MobileDateRangePicker
          value={dateRange}
          onChange={(v) => {
            setDateRange(v && v[0] && v[1] ? [v[0], v[1]] : null);
            setPageNumber(1);
          }}
          format="DD/MM/YYYY"
          style={{ width: isMobile ? '100%' : 240 }}
        />
        <Select
          placeholder="Categoría"
          allowClear
          style={{ width: isMobile ? '100%' : 150 }}
          value={categoryId}
          onChange={(v) => {
            setCategoryId(v);
            setPageNumber(1);
          }}
          options={(categories || []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <Input
          placeholder="Proveedor o notas"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: isMobile ? '100%' : 200 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPageNumber(1);
          }}
        />
        {/* Los filtros avanzados son de auditoría (qué caja, qué método, qué falta por
            comprobar). Con alcance propio no hay nada que auditar: todo es tuyo. */}
        {seesEverything && (
          <Button type="text" onClick={() => setMoreFiltersOpen(!moreFiltersOpen)}>
            {moreFiltersOpen ? '− Filtros' : '+ Filtros'}
          </Button>
        )}
        <div style={{ flex: 1 }} />
        {seesEverything && (
          <Dropdown menu={{ items: exportMenuItems }} disabled={isExporting}>
            <Button icon={<DownloadOutlined />} loading={isExporting}>
              {!isMobile && 'Exportar'}
            </Button>
          </Dropdown>
        )}
        <Tooltip title="Refrescar">
          <Button type="text" icon={<ReloadOutlined />} onClick={() => refetch()} />
        </Tooltip>
      </div>

      {seesEverything && moreFiltersOpen && (
        <div className="fin-filters">
          <Select
            placeholder="Pagado desde caja"
            allowClear
            style={{ width: isMobile ? '100%' : 180 }}
            value={paidFromCash === undefined ? undefined : paidFromCash ? 'yes' : 'no'}
            onChange={(v) => {
              setPaidFromCash(v === undefined ? undefined : v === 'yes');
              setPageNumber(1);
            }}
            options={[
              { value: 'yes', label: 'Pagado desde caja' },
              { value: 'no', label: 'Sin caja' },
            ]}
          />
          <Select
            placeholder="Método de pago"
            allowClear
            style={{ width: isMobile ? '100%' : 180 }}
            value={paymentMethodFilter}
            onChange={(v) => {
              setPaymentMethodFilter(v);
              setPageNumber(1);
            }}
            options={[
              // "Sin método" pinned at the top — the customer's most common use case is
              // surfacing pre-existing expenses with PaymentMethodId IS NULL so they can
              // reclassify them.
              { value: 'none', label: 'Sin método' },
              ...(paymentMethods || []).map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          <Select
            placeholder="Comprobante"
            allowClear
            style={{ width: isMobile ? '100%' : 170 }}
            value={attachmentFilter}
            onChange={(v) => {
              setAttachmentFilter(v);
              setPageNumber(1);
            }}
            options={[
              // "Sin comprobante" primero: adjuntar es opcional, así que este filtro es
              // la herramienta con la que el dueño persigue los faltantes.
              { value: 'no', label: 'Sin comprobante' },
              { value: 'yes', label: 'Con comprobante' },
            ]}
          />
          <Button type="link" size="small" onClick={clearFilters}>
            Limpiar filtros
          </Button>
        </div>
      )}

      {/* KPIs del período (conjunto filtrado completo, solo activos).
          El backend ya manda `summary` en null para el alcance propio — son totales del
          NEGOCIO, no de su captura. La guarda explícita está igual porque una tarjeta que
          aparece o no según llegue un campo es un accidente esperando a pasar. */}
      {seesEverything && summary && (
        <div className="fin-kpis">
          <div className="fin-kpi">
            <div className="fin-kpi__label">Total del período</div>
            <div className="fin-kpi__value">{money(summary.activeTotal)}</div>
            <div className="fin-kpi__sub">
              {summary.activeCount} gasto{summary.activeCount !== 1 ? 's' : ''} activo{summary.activeCount !== 1 ? 's' : ''}
            </div>
          </div>
          <div className="fin-kpi">
            <div className="fin-kpi__label">Desde caja</div>
            <div className="fin-kpi__value">{money(summary.cashTotal)}</div>
            <div className="fin-kpi__sub">{summary.cashCount} gasto{summary.cashCount !== 1 ? 's' : ''}</div>
          </div>
          <div className="fin-kpi">
            <div className="fin-kpi__label">Otros métodos</div>
            <div className="fin-kpi__value">{money(summary.otherTotal)}</div>
            <div className="fin-kpi__sub">{summary.otherCount} gasto{summary.otherCount !== 1 ? 's' : ''}</div>
          </div>
          <div
            className="fin-kpi fin-kpi--clickable"
            onClick={focusNoMethod}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && focusNoMethod()}
          >
            <div className="fin-kpi__label">Sin método</div>
            <div className="fin-kpi__value">{money(summary.noMethodTotal)}</div>
            <div className="fin-kpi__sub">
              {summary.noMethodCount > 0
                ? `${summary.noMethodCount} por clasificar →`
                : 'todo clasificado ✓'}
            </div>
          </div>
          {/* Con adjuntos opcionales, esta card es la que hace que la feature sirva:
              sin ella el dueño no tiene cómo auditar y la captura de comprobantes
              queda a discreción de cada empleado sin que nadie lo note. */}
          <div
            className="fin-kpi fin-kpi--clickable"
            onClick={focusNoReceipt}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && focusNoReceipt()}
          >
            <div className="fin-kpi__label">Sin comprobante</div>
            <div className="fin-kpi__value">{money(summary.noReceiptTotal)}</div>
            <div className="fin-kpi__sub">
              {summary.noReceiptCount > 0
                ? `${summary.noReceiptCount} sin comprobar →`
                : 'todo comprobado ✓'}
            </div>
          </div>
        </div>
      )}

      {hasNoCategories && (
        <Alert
          type="warning"
          showIcon
          message="No hay categorías de gasto configuradas. Contacta a soporte."
          style={{ marginBottom: 16 }}
        />
      )}

      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la lista de gastos."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="fin-panel">
        <Table<ExpenseDto>
          rowKey="id"
          columns={columns}
          dataSource={expensesPage?.items || []}
          loading={isFetching}
          size="small"
          rowClassName={(row) => (row.status === 'Cancelled' ? 'fin-row--cancelled' : '')}
          scroll={isMobile ? undefined : { x: 960 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  statusFilter !== 'active' ||
                  categoryId !== undefined ||
                  paidFromCash !== undefined ||
                  paymentMethodFilter !== undefined ||
                  attachmentFilter !== undefined ||
                  search.trim() !== '' ? (
                    <Space direction="vertical" size={4}>
                      <span>Ningún gasto coincide con los filtros.</span>
                      <Button type="link" size="small" onClick={clearFilters}>
                        Limpiar filtros
                      </Button>
                    </Space>
                  ) : (
                    <span>
                      Aún no hay gastos en este rango. Captura uno con el botón{' '}
                      <Text strong>Nuevo gasto</Text>.
                    </span>
                  )
                }
              />
            ),
          }}
          pagination={{
            current: pageNumber,
            pageSize,
            total: expensesPage?.totalCount || 0,
            showSizeChanger: !isMobile,
            pageSizeOptions: ['25', '50', '100', '200'],
            showTotal: isMobile ? undefined : (total) => `${total} gasto(s)`,
            simple: isMobile,
            onChange: (page, size) => {
              setPageNumber(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      {/* Dialogs */}
      <ExpenseFormDialog
        open={createOpen}
        restaurantId={restaurantId}
        categories={categories || []}
        capture={capture}
        onClose={() => {
          setCreateOpen(false);
          setCapture(null);
        }}
      />

      <ExpenseCaptureFlow
        restaurantId={restaurantId}
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        onCaptured={(result) => {
          setCaptureOpen(false);
          setCapture(result);
          setCreateOpen(true);
        }}
        onManualInstead={() => {
          // Rechazar tiene salida: se abre el form de siempre, vacío. El botón de
          // "+ Nuevo gasto" tampoco se toca ni se esconde nunca.
          setCaptureOpen(false);
          setCapture(null);
          setCreateOpen(true);
        }}
      />

      <ExpenseEditDialog
        expense={editingExpense}
        restaurantId={restaurantId}
        onClose={() => setEditingExpense(null)}
        onRequestCancel={(e) => {
          setEditingExpense(null);
          setCancellingExpense(e);
        }}
      />

      <ExpenseCancelDialog
        expense={cancellingExpense}
        restaurantId={restaurantId}
        onClose={() => setCancellingExpense(null)}
      />

      {/* Visor desde el clip de la fila: ver los comprobantes sin entrar a editar. */}
      <Modal
        title={
          viewingAttachmentsOf
            ? `Comprobantes · ${viewingAttachmentsOf.vendor || viewingAttachmentsOf.categoryName}`
            : 'Comprobantes'
        }
        open={!!viewingAttachmentsOf}
        onCancel={() => setViewingAttachmentsOf(null)}
        footer={
          <Button onClick={() => setViewingAttachmentsOf(null)} block={isMobile}>
            Cerrar
          </Button>
        }
        // Ver ExpenseFormDialog: la geometría móvil del modal es global (App.css).
        width={560}
        destroyOnClose
      >
        {viewingAttachmentsOf && (
          <ExpenseAttachmentsViewer
            restaurantId={restaurantId}
            expenseId={viewingAttachmentsOf.id}
          />
        )}
      </Modal>
    </div>
  );
};

export default ExpensesTab;
